import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardBody } from 'reactstrap';
import AddEditActivity from '../../components/AddEditActivity';
import cs_analysis_fetch from '../../utils/cs_analysis_fetch';
import general_fetch from '../../utils/fetch';
import { resolve_context } from '../StoreDashboard/ClientHealthTab/TaskLogActivity';
import { format_datetime_dd_mmm_yy as fmt_datetime } from '../../utils/format_date_dd_mmm_yy';
import fmt_date from '../../utils/format_date_dd_mmm_yy';
import { activity_for_note, decision_message, expiry_label, group_offers_by_meeting, note_decision_message, short_quote, without_offer } from './meeting_commitments_helpers';

/**
 * "From your meetings" — the rep's own promises from recent client calls, as
 * cs-analysis heard them (docs/design-meeting-commitments.md §3). The same
 * offers the Google Chat DM carries; the CSP is the durable copy, so a missed
 * DM loses nothing. Each offer has two buttons: *Add to my tasks* creates the
 * task on the rep (and the Tasks list below refreshes), *Not needed* drops it.
 * Untouched offers go quiet after the api's expireDays. Nothing else exists
 * until the rep decides — no badge, no count anywhere else.
 *
 * A call's drafted notes (§9: summary + next steps) sit under the call with
 * *Add to meeting notes*: it opens the meeting's own activity in the CSP's
 * activity dialog with the draft appended to its note; the rep checks, edits
 * and presses Update (the CSP's own activity/update, as the rep), and only then
 * is the draft marked saved. *Dismiss* drops the draft.
 *
 * Props: rep_email (admin impersonation → ?rep_email=), store_id (narrow to
 * one client), on_changed (called after an accept so the parent reloads).
 */
const MeetingCommitments = ({ rep_email = null, store_id = null, on_changed, admin = false, reps = [] }) => {
    // An admin who is not a CS rep has no offers of their own: pick a rep to see theirs (the api allows admins to read and decide for any rep).
    const [picked_rep, set_picked_rep] = useState('');
    const effective_rep = rep_email || (admin ? picked_rep : '') || null;
    const [offers, set_offers] = useState(null);
    const [expire_days, set_expire_days] = useState(null);
    const [err, set_err] = useState(null);
    const [busy_id, set_busy_id] = useState(null);
    const [note, set_note] = useState(null);
    const [drafts, set_drafts] = useState([]);
    // The meeting activity open in the dialog: { draft, activity, ctx }.
    const [editing, set_editing] = useState(null);

    const load = useCallback(async () => {
        set_err(null);
        try {
            const query = {};
            if (effective_rep) query.rep_email = effective_rep;
            if (store_id) query.storeId = store_id;
            const r = await cs_analysis_fetch({ path: '/api/v1/meeting-commitments/mine', query });
            set_offers(r && Array.isArray(r.offers) ? r.offers : []);
            set_drafts(r && Array.isArray(r.notes) ? r.notes : []);
            set_expire_days(r && r.expireDays ? r.expireDays : null);
        } catch (e) {
            set_err(e.message || String(e));
            set_offers([]);
            set_drafts([]);
        }
    }, [effective_rep, store_id]);

    useEffect(() => { load(); }, [load]);

    const decide = useCallback(async (id, action) => {
        if (busy_id) return;
        set_busy_id(id);
        set_err(null);
        try {
            const r = await cs_analysis_fetch({ path: `/api/v1/meeting-commitments/${encodeURIComponent(id)}/decide`, method: 'POST', body: { action } });
            set_offers((prev) => without_offer(prev, id));
            set_note(decision_message(action, r && r.outcome));
            if (action === 'accept' && on_changed) on_changed();
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy_id(null);
        }
    }, [busy_id, on_changed]);

    const decide_note = useCallback(async (id, action) => {
        if (busy_id) return;
        set_busy_id(id);
        set_err(null);
        try {
            await cs_analysis_fetch({ path: `/api/v1/meeting-commitments/notes/${encodeURIComponent(id)}/decide`, method: 'POST', body: { action } });
            set_drafts((prev) => prev.filter((d) => d && d.id !== id));
            set_note(note_decision_message(action));
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy_id(null);
        }
    }, [busy_id]);

    // Opens the meeting's activity, as the CSP's own dialog, with the draft appended to its note.
    const open_note = useCallback(async (draft) => {
        if (busy_id) return;
        set_busy_id(draft.id);
        set_err(null);
        set_note(null);
        try {
            const ctx = await resolve_context(draft.storeId, {});
            if (!ctx.user_details || !ctx.user_details.id) throw new Error('Could not resolve the logged-in user');
            const activities = await general_fetch({ url: 'cs_backend/activity/get_by_org', body: { org_id: draft.storeId } });
            const activity = activity_for_note(activities, draft);
            if (!activity) throw new Error("The meeting's activity was not found (deleted?). Dismiss the draft, or copy it from here.");
            set_editing({ draft, activity, ctx });
        } catch (e) {
            set_err((e && e.message) || (e && e.err && e.err.message) || 'Could not open the activity');
        } finally {
            set_busy_id(null);
        }
    }, [busy_id]);

    const rep_picker = admin && !rep_email && reps.length > 0 ? (
        <select value={picked_rep} onChange={(e) => { set_note(null); set_picked_rep(e.target.value); }} style={{ fontSize: 12, marginLeft: 8 }} data-testid='meeting-offers-rep'>
            <option value=''>Show a rep's offers…</option>
            {reps.filter((r) => r.email).map((r) => <option key={r.email} value={r.email}>{r.name}</option>)}
        </select>
    ) : null;
    if (offers === null && !rep_picker) return null;
    const groups = group_offers_by_meeting(offers || [], drafts);
    if (groups.length === 0 && !note && !err && !rep_picker) return null;

    return (
        <Card style={{ marginBottom: 12, borderLeft: '4px solid #8e44ad' }}>
            <CardBody style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>From {effective_rep && admin ? `${effective_rep.split('@')[0]}'s` : 'your'} meetings{rep_picker}</div>
                    <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                        Things you said you would do on recent calls, in your own words{drafts.length > 0 ? ", and the calls' drafted notes" : ''}. Nothing is created {drafts.length > 0 ? 'or saved ' : ''}unless you add it
                        {expire_days ? `; untouched ${drafts.length > 0 ? 'items' : 'offers'} go quiet after ${expire_days} days` : ''}.
                    </div>
                </div>
                {err ? <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{err}</div> : null}
                {admin && effective_rep && offers && offers.length === 0 && !note ? <div style={{ fontSize: 12, color: '#7f8c8d', marginTop: 6 }}>No open offers for this rep right now.</div> : null}
                {note ? <div style={{ color: '#27ae60', fontSize: 12, marginTop: 6 }}>{note}</div> : null}
                {groups.map((g) => (
                    <div key={g.key} style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {g.storeId ? (
                                <a href={`/home/store_dashboard/${encodeURIComponent(g.storeId)}`} target='_blank' rel='noreferrer noopener'>{g.clientName}</a>
                            ) : g.clientName}
                            <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 400, marginLeft: 8 }}>call on {g.meetingAt ? fmt_datetime(g.meetingAt) : '—'}</span>
                        </div>
                        {g.note ? (
                            <div style={{ padding: '8px 0', borderTop: '1px solid #f0f2f5' }} data-testid='meeting-note-draft'>
                                <div style={{ fontSize: 12, color: '#7f8c8d', marginBottom: 4 }}>Notes drafted from the call — check them before you save</div>
                                <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#faf7fc', border: '1px solid #ece4f2', borderRadius: 4, padding: 8, maxHeight: 180, overflowY: 'auto' }}>{g.note.text}</div>
                                <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                                    <Button size='sm' color='primary' disabled={busy_id === g.note.id || !g.note.activityId || !g.note.storeId} onClick={() => open_note(g.note)}>Add to meeting notes</Button>
                                    <Button size='sm' outline color='secondary' disabled={busy_id === g.note.id} onClick={() => decide_note(g.note.id, 'dismiss')}>Dismiss</Button>
                                    <span style={{ fontSize: 11, color: '#95a5a6' }}>{expiry_label(g.note.expiresAt)}</span>
                                </div>
                            </div>
                        ) : null}
                        {g.offers.map((o) => (
                            <div key={o.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid #f0f2f5' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 500 }}>
                                        {o.summary}
                                        {o.dueDate ? <span style={{ fontSize: 12, color: '#c0392b', marginLeft: 8 }}>due {fmt_date(o.dueDate)}</span> : null}
                                        {o.module ? <span style={{ fontSize: 11, color: '#7f8c8d', marginLeft: 8 }}>{o.module}</span> : null}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#566573', fontStyle: 'italic', marginTop: 2 }} title={o.quote}>“{short_quote(o.quote)}”</div>
                                    <div style={{ fontSize: 11, color: '#95a5a6', marginTop: 2 }}>{expiry_label(o.expiresAt)}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    <Button size='sm' color='primary' disabled={busy_id === o.id} onClick={() => decide(o.id, 'accept')}>Add to my tasks</Button>
                                    <Button size='sm' outline color='secondary' disabled={busy_id === o.id} onClick={() => decide(o.id, 'ignore')}>Not needed</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </CardBody>
            {editing ? (
                <AddEditActivity
                    open={!!editing}
                    toggle={() => set_editing(null)}
                    mode='edit'
                    active_activity={editing.activity}
                    note_prefill={editing.draft.text}
                    on_updated={() => decide_note(editing.draft.id, 'saved')}
                    cs_org_users={editing.ctx.cs_org_users}
                    user_details={editing.ctx.user_details}
                    get_activities={() => {}}
                    all_orgs={[]}
                    set_page_loader={() => {}}
                    current_org_users={editing.ctx.org_users}
                    current_org={editing.ctx.org}
                    open_create_activity_modal={() => {}}
                    cs_executive_id_of_org={editing.ctx.org && editing.ctx.org.customer_success_executive_id}
                />
            ) : null}
        </Card>
    );
};

export default MeetingCommitments;
